import { cn } from "@/lib/utils";
import { getPasswordStrengthColor, getPasswordStrengthBg, type PasswordValidationResult } from "@/lib/passwordValidation";

interface PasswordStrengthMeterProps {
  validation: PasswordValidationResult;
  className?: string;
}

export const PasswordStrengthMeter = ({ validation, className }: PasswordStrengthMeterProps) => {
  const { strength, score, errors } = validation;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn("h-full transition-all duration-300", getPasswordStrengthBg(strength))}
            style={{ width: `${score}%` }}
          />
        </div>
        <span className={cn("text-sm font-medium capitalize", getPasswordStrengthColor(strength))}>
          {strength}
        </span>
      </div>

      {/* Error messages */}
      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error, index) => (
            <p key={index} className="text-xs text-destructive">
              • {error}
            </p>
          ))}
        </div>
      )}

      {/* Requirements checklist */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p className="font-medium">Password Requirements:</p>
        <div className="grid grid-cols-2 gap-1">
          <span className={score >= 25 ? "text-green-600" : ""}>✓ 12+ characters</span>
          <span className={/[A-Z]/.test('') ? "text-green-600" : ""}>✓ Uppercase letter</span>
          <span className={/[a-z]/.test('') ? "text-green-600" : ""}>✓ Lowercase letter</span>
          <span className={/\d/.test('') ? "text-green-600" : ""}>✓ Number</span>
          <span className={/[!@#$%^&*(),.?":{}|<>]/.test('') ? "text-green-600" : ""}>✓ Special character</span>
        </div>
      </div>
    </div>
  );
};